import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import type { Condition } from '@repo/api-gw-sdk';
import { useState, type CSSProperties, type UIEventHandler } from 'react';

import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import {
  PropertyType,
  isValueCondition,
  type FilterProperty,
} from '@/types/advanceFilter';

import {
  backupStatus,
  controlId,
  controlLevel,
  mutedControlId,
} from './properties';

import {
  BackupStatusFilterRow,
  AdditionalStatuses,
  ViolationsDetectedFilterRow,
} from '../backupStatus/backupStatusFilterRow';
import { Loader } from '../layout/loading';
import { SearchTextBox } from '../search/searchTextBox';
import { TransactionalTextField } from '../shared/transactionalTextField';
import { Virtuoso } from '../shared/virtuoso';
import { TextDetailsTuple } from '../text/textDetailsTuple';

export interface FilterProps {
  property: FilterProperty;
  conditions: Condition[];
  'data-testid'?: string;
  onChange: (conditions: Condition[]) => void;
  renderDrillDownFilter?: (
    title: string,
    subtitle: string,
    component: (props: { filter: Condition }) => React.ReactElement
  ) => void;
}

export const FreeTextFilter = ({
  placeholder,
  property,
  conditions,
  onChange,
  'data-testid': dataTestId,
  showSearchIcon = true,
}: FilterProps & { placeholder: string; showSearchIcon?: boolean }) => {
  const condition = conditions.find(
    (x) => isValueCondition(x) && x.property === property.name
  );

  return (
    <FormControl variant='outlined' fullWidth>
      <TransactionalTextField
        showSearchIcon={showSearchIcon}
        showClearIcon
        placeholder={placeholder}
        allowEmptyValue
        data-testid={dataTestId}
        initValue={(isValueCondition(condition) && condition?.value?.[0]) || ''}
        onChange={(value) => {
          const cleanValue = (value || '').trim();
          onChange(
            conditions
              .filter(
                (x) => isValueCondition(x) && x.property !== property.name
              )
              .concat(
                cleanValue
                  ? [
                      {
                        property: property.name,
                        operator: ConditionOperatorEnum.Contains,
                        value: [cleanValue],
                      },
                    ]
                  : []
              )
          );
        }}
      />
    </FormControl>
  );
};

interface OptionType {
  id: string;
  name?: string;
  Image?: React.ReactNode;
  description?: React.ReactNode;
  isGroupTitle?: boolean;
}

export const MultiSelectFilter = ({
  property,
  conditions,
  onChange,
  options,
  'data-testid': dataTestId,
  showSelectAll,
  virtuosoStyles,
  onVirtuosoScroll,
  showNameFirst,
  shouldShowId,
}: FilterProps & {
  showSelectAll?: boolean;
  options: OptionType[];
  virtuosoStyles?: CSSProperties;
  onVirtuosoScroll?: UIEventHandler<HTMLDivElement>;
  showNameFirst?: boolean;
  shouldShowId?: boolean;
}) => {
  const condition = conditions.find(
    (x) => isValueCondition(x) && x.property === property.name
  );

  const selectedResources =
    (isValueCondition(condition) && condition?.value) || [];

  const onItemChange = (option: OptionType, newValue: boolean) => {
    const values = newValue
      ? [...selectedResources, option.id]
      : selectedResources.filter((x) => x !== option.id);

    const existingCondition = conditions.find(
      (x) => isValueCondition(x) && x.property === property.name
    );

    onChange(
      conditions
        .filter((x) => x !== existingCondition)
        .concat(
          values.length
            ? [
                {
                  property: property.name,
                  operator: existingCondition
                    ? existingCondition.operator
                    : property.type === PropertyType.MultipleValues
                      ? ConditionOperatorEnum.ContainsAnyOf
                      : ConditionOperatorEnum.In,
                  value: values,
                },
              ]
            : []
        )
    );
  };

  const allOptions = showSelectAll
    ? [{ id: 'select-all' }, ...options]
    : options;

  if (allOptions.length < 100) {
    return (
      <Stack gap='4px'>
        {allOptions.map((option) =>
          option.id === 'select-all' ? (
            <SelectAllFilterItem
              key={option.id}
              options={options}
              selectedResources={selectedResources}
              onChange={onChange}
              conditions={conditions}
              property={property}
            />
          ) : (
            <MultiSelectFilterItem
              key={option.id}
              option={option}
              dataTestId={dataTestId}
              onChange={(newValue) => onItemChange(option, newValue)}
              isChecked={selectedResources.includes(option.id)}
              shouldShowId={!!shouldShowId}
              showNameFirst={!!showNameFirst}
            />
          )
        )}
      </Stack>
    );
  }

  return (
    <Virtuoso
      style={virtuosoStyles || { height: '100%' }}
      data={allOptions}
      onScroll={onVirtuosoScroll}
      itemContent={(index, option) =>
        option.id === 'select-all' ? (
          <SelectAllFilterItem
            key={option.id}
            options={options}
            selectedResources={selectedResources}
            onChange={onChange}
            conditions={conditions}
            property={property}
          />
        ) : (
          <MultiSelectFilterItem
            key={option.id}
            option={option}
            dataTestId={dataTestId}
            onChange={(newValue) => onItemChange(option, newValue)}
            isChecked={selectedResources.includes(option.id)}
            shouldShowId={!!shouldShowId}
            showNameFirst={!!showNameFirst}
          />
        )
      }
    />
  );
};

const SelectAllFilterItem = ({
  options,
  selectedResources,
  onChange,
  conditions,
  property,
}: {
  selectedResources: string[];
  onChange: (conditions: Condition[]) => void;
  conditions: Condition[];
  property: FilterProperty;
  options: OptionType[];
}) => {
  const selectableOptions = options.filter((x) => !x.isGroupTitle);

  return (
    <MultiSelectFilterItem
      shouldShowId={false}
      showNameFirst={false}
      option={{
        id: 'select-all',
        name: `Select all (${selectableOptions.length})`,
      }}
      onChange={(newValue: boolean) => {
        const values = newValue ? selectableOptions.map((x) => x.id) : [];
        onChange(
          conditions
            .filter((x) => isValueCondition(x) && x.property !== property.name)
            .concat(
              values.length
                ? [
                    {
                      property: property.name,
                      operator:
                        property.type === PropertyType.MultipleValues
                          ? ConditionOperatorEnum.ContainsAnyOf
                          : ConditionOperatorEnum.In,
                      value: values,
                    },
                  ]
                : []
            )
        );
      }}
      isChecked={options.every((x) => selectedResources.includes(x.id))}
    />
  );
};

const MultiSelectFilterItem = ({
  option,
  dataTestId,
  onChange,
  isChecked,
  shouldShowId,
  showNameFirst,
}: {
  option: OptionType;
  dataTestId?: string;
  isChecked: boolean;
  onChange: (newValue: boolean) => void;
  shouldShowId: boolean;
  showNameFirst: boolean;
}) => {
  if (option.isGroupTitle) {
    return (
      <Typography variant='overline' paddingY='8px'>
        {option.name}
      </Typography>
    );
  }

  return (
    <FormControlLabel
      key={option.id}
      className='group/checkbox'
      sx={{
        alignItems: 'center',
        display: 'flex',
        width: '100%',
      }}
      control={
        <Checkbox
          slotProps={{
            input: {
              'data-testid': `${dataTestId ? `${dataTestId}-` : ''}${option.id}`,
            } as Record<string, string>,
          }}
          className='p-[8px] mr-[4px]'
          size='small'
          checked={isChecked}
          onChange={(event, value) => onChange(value)}
        />
      }
      label={
        <Stack
          direction='row'
          alignItems='center'
          className='w-[216px] relative'
          gap='12px'
        >
          {option.Image}
          <TextDetailsTuple
            primaryProp={
              shouldShowId
                ? showNameFirst
                  ? option.name
                  : option.id
                : undefined
            }
            secondaryProp={showNameFirst ? option.id : option.name}
          />
          {option.description && (
            <Tooltip
              title={option.description}
              slotProps={{
                tooltip: {
                  sx: {
                    padding: '16px',
                  },
                },
              }}
            >
              <i className='opacity-0 group-hover/checkbox:opacity-100 transition-opacity absolute right-0 material-symbols-help-outline-rounded h-[20px] w-[20px] p-[2px] cursor-default' />
            </Tooltip>
          )}
        </Stack>
      }
    />
  );
};

export const BackupStatusFilter = ({
  conditions,
  onChange,
  renderDrillDownFilter,
  showAdditionalStatuses,
}: FilterProps & { showAdditionalStatuses: boolean }) => {
  const { dlsgFrontend } = useFeatureFlags();
  if (!renderDrillDownFilter) {
    return null;
  }

  const valueConditions = conditions.filter(isValueCondition);

  const backupStatusConditions = valueConditions.filter(
    (x) => x.property === backupStatus.name
  );

  const includedBackupStatusCondition = backupStatusConditions.find(
    (x) => x.operator === ConditionOperatorEnum.In
  );

  const excludedBackupStatusCondition = backupStatusConditions.find(
    (x) => x.operator === ConditionOperatorEnum.NotIn
  );

  const controlLevelCondition = valueConditions.find(
    (x) => x.property === controlLevel.name
  );

  const selectedStatuses = includedBackupStatusCondition?.value || [];

  const selectedLevel = controlLevelCondition?.value?.[0] || undefined;

  const toggleBackupStatus = (status: BackupStatus) => {
    const cleanConditions = valueConditions.filter(
      (x) =>
        x !== includedBackupStatusCondition &&
        x.property !== controlLevel.name &&
        x.property !== controlId.name &&
        x.property !== mutedControlId.name
    );

    if (selectedStatuses.includes(status)) {
      const updatedStatuses = selectedStatuses.filter((x) => x !== status);
      onChange(
        cleanConditions.concat(
          updatedStatuses.length === 0
            ? []
            : [
                {
                  property: backupStatus.name,
                  operator: ConditionOperatorEnum.In,
                  value: updatedStatuses,
                },
              ]
        )
      );
    } else {
      onChange(
        cleanConditions.concat({
          property: backupStatus.name,
          operator: ConditionOperatorEnum.In,
          value: selectedStatuses.concat([status]),
        })
      );
    }
  };

  const selectControlLevel = (severity: string) => {
    const cleanConditions = valueConditions.filter(
      (x) =>
        x !== includedBackupStatusCondition &&
        x.property !== controlLevel.name &&
        x.property !== controlId.name &&
        x.property !== mutedControlId.name
    );

    onChange(
      cleanConditions.concat({
        property: controlLevel.name,
        operator: ConditionOperatorEnum.Equals,
        value: [severity.toUpperCase()],
      })
    );
  };

  return (
    <Stack>
      <BackupStatusFilterRow
        backupStatus={BackupStatus.Protected}
        toggleBackupStatus={toggleBackupStatus}
        selectedBackupStatuses={selectedStatuses}
      />
      {dlsgFrontend && (
        <BackupStatusFilterRow
          backupStatus={BackupStatus.Dlsg}
          toggleBackupStatus={toggleBackupStatus}
          selectedBackupStatuses={selectedStatuses}
        />
      )}
      <BackupStatusFilterRow
        backupStatus={BackupStatus.AllViolationsMuted}
        toggleBackupStatus={toggleBackupStatus}
        selectedBackupStatuses={selectedStatuses}
      />
      <ViolationsDetectedFilterRow
        backupStatus={BackupStatus.ViolationsDetected}
        toggleBackupStatus={toggleBackupStatus}
        selectControlLevel={selectControlLevel}
        selectedBackupStatuses={selectedStatuses}
        selectedLevel={selectedLevel?.toLowerCase()}
        renderDrillDownFilter={renderDrillDownFilter}
        conditions={conditions}
        onFilterChange={onChange}
      />
      <BackupStatusFilterRow
        backupStatus={BackupStatus.ActionRequired}
        toggleBackupStatus={toggleBackupStatus}
        selectedBackupStatuses={selectedStatuses}
      />
      <Divider sx={{ margin: '12px -24px' }} />
      <BackupStatusFilterRow
        backupStatus={BackupStatus.GenericBackups}
        toggleBackupStatus={toggleBackupStatus}
        selectedBackupStatuses={selectedStatuses}
      />
      <BackupStatusFilterRow
        backupStatus={BackupStatus.NotBackedUp}
        toggleBackupStatus={toggleBackupStatus}
        selectedBackupStatuses={selectedStatuses}
      />
      <BackupStatusFilterRow
        backupStatus={BackupStatus.InitialClassification}
        toggleBackupStatus={toggleBackupStatus}
        selectedBackupStatuses={selectedStatuses}
      />
      <BackupStatusFilterRow
        backupStatus={BackupStatus.Terminated}
        toggleBackupStatus={toggleBackupStatus}
        selectedBackupStatuses={selectedStatuses}
      />
      {!excludedBackupStatusCondition?.value?.includes(
        BackupStatus.Disconnected
      ) && (
        <BackupStatusFilterRow
          backupStatus={BackupStatus.Disconnected}
          toggleBackupStatus={toggleBackupStatus}
          selectedBackupStatuses={selectedStatuses}
        />
      )}
      {!excludedBackupStatusCondition?.value?.includes(
        BackupStatus.ExcludedFromBackup
      ) && (
        <BackupStatusFilterRow
          backupStatus={BackupStatus.ExcludedFromBackup}
          toggleBackupStatus={toggleBackupStatus}
          selectedBackupStatuses={selectedStatuses}
        />
      )}
      {!excludedBackupStatusCondition?.value?.includes(
        BackupStatus.Unsupported
      ) && (
        <BackupStatusFilterRow
          backupStatus={BackupStatus.Unsupported}
          toggleBackupStatus={toggleBackupStatus}
          selectedBackupStatuses={selectedStatuses}
        />
      )}
      {!excludedBackupStatusCondition?.value?.includes(
        BackupStatus.LimitExceeded
      ) && (
        <BackupStatusFilterRow
          backupStatus={BackupStatus.LimitExceeded}
          toggleBackupStatus={toggleBackupStatus}
          selectedBackupStatuses={selectedStatuses}
        />
      )}
      {showAdditionalStatuses && (
        <>
          <Divider sx={{ margin: '12px -24px' }} />
          <AdditionalStatuses
            valueConditions={valueConditions}
            onChange={onChange}
            excludedBackupStatusCondition={excludedBackupStatusCondition}
            includedBackupStatusCondition={includedBackupStatusCondition}
          />
        </>
      )}
    </Stack>
  );
};

export const FullPanelMultiSelectFilter = (
  props: FilterProps & {
    onDoneClick: () => void;
    hideSearch?: boolean;
    isLoading: boolean;
    entityName: string;
    options: OptionType[];
    shouldShowId?: boolean;
    showNameFirst?: boolean;
  }
) => {
  const [filter, setFilter] = useState('');
  const [isScrolledVertically, setScrolledVertically] = useState(false);

  if (props.isLoading) {
    return <Loader />;
  }

  const condition = props.conditions.find(
    (x) => isValueCondition(x) && x.property === props.property.name
  );

  const selectedResources =
    (isValueCondition(condition) && condition?.value) || [];

  const filteredOptions = props.options.filter(
    (x) =>
      !filter ||
      x.name?.toLowerCase().includes(filter.toLowerCase()) ||
      (props.shouldShowId && x.id?.toLowerCase().includes(filter.toLowerCase()))
  );

  const filteredOptionsCount = filteredOptions.filter(
    (x) => !x.isGroupTitle
  ).length;

  const onScroll: UIEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLDivElement;
    if (typeof target.scrollTop === 'number') {
      const isScrolled = target.scrollTop > 0;
      if (isScrolled !== isScrolledVertically) {
        setScrolledVertically(isScrolled);
      }
    }
  };

  return (
    <Stack className='h-full overflow-hidden'>
      {!props.hideSearch && (
        <Stack
          className='px-[24px] pb-[24px]'
          sx={{
            borderBottom: '1px solid var(--mui-palette-divider)',
            boxShadow: isScrolledVertically
              ? 'var(--mui-customShadows-panel)'
              : undefined,
          }}
        >
          <SearchTextBox
            value={filter}
            onChange={setFilter}
            placeholder={`Search by name or ID`}
          />
        </Stack>
      )}
      <Box
        flexGrow={1}
        className='overflow-auto px-[24px] py-[12px]'
        onScroll={onScroll}
      >
        {filteredOptionsCount === 0 && (
          <Typography variant='body1' className='my-[12px]'>
            {`No matching values found. Try a new search term.`}
          </Typography>
        )}
        {filteredOptionsCount > 0 && (
          <MultiSelectFilter
            {...props}
            showSelectAll={!props.hideSearch && !!filter}
            options={filteredOptions}
            onVirtuosoScroll={onScroll}
            showNameFirst={props.showNameFirst}
            shouldShowId={props.shouldShowId}
          />
        )}
      </Box>
      {!!selectedResources.length && (
        <Stack
          direction='row'
          alignItems='center'
          className='px-[24px] py-[16px]'
          justifyContent='space-between'
          sx={{
            boxShadow: 'var(--mui-customShadows-panelUp)',
          }}
        >
          <Typography>{`${selectedResources.length} ${props.entityName}s selected`}</Typography>
          <Stack direction='row' alignItems='center'>
            <Tooltip title='Clear all'>
              <IconButton
                onClick={() =>
                  props.onChange(
                    props.conditions.filter((x) => x !== condition)
                  )
                }
              >
                <i className='material-symbols-filter-alt-off-outline-rounded text-[var(--mui-palette-text-primary)] h-[20px] w-[20px]'></i>
              </IconButton>
            </Tooltip>
            <Button
              className='ml-[12px]'
              variant='outlined'
              onClick={() => props.onDoneClick()}
            >
              Done
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
